import React, { Component } from 'react';
import { withTranslation, Translation } from 'react-i18next';
import PropTypes from 'prop-types';
import { titleize, unescapeHTML } from 'underscore.string';
import CustomerSearch from 'components/site/customer-search';
import LocationAutosuggest from 'components/site/location-autosuggest';
import OrdersActionCreators from '../../../actions/OrdersActionCreators';
import Workflow from './Workflow';
import CancelOrderModal from './CancelOrderModal';
import { sluggify } from 'utils/localization/utils';
import { showToast } from 'stores/ToastStore';
import {
  Button as UiButton,
  DropdownMenu as UiDropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@cargomatic/ui';
import { Pencil, ChevronDown } from 'lucide-react';
import { OverrideBillingStatusModal } from 'components/modules/shipments/OverrideBillingStatusModal';
import SessionStore from 'stores/SessionStore';
import BillingStatusRenderer from 'components/v2/BillingStatusRenderer';
import { isLegManagementEnabled } from 'helpers/legs-helper';
import ShipmentStatusBadge from 'components/v2/ShipmentStatusBadge';
import { OrderDistance } from 'components/v2/OrderDistance';

const closedStatuses = ['canceled', 'complete'];
const isBillingStatusAdmin = SessionStore.isBillingStatusAdmin();
const isBillingStatusManager = SessionStore.isBillingStatusManager();

const canReopen = (order) => {
  if (!order) {
    return false;
  }
  return closedStatuses.includes(order.status);
};

const canOverrideBillingStatus = () => {
  return isBillingStatusManager || isBillingStatusAdmin;
};

const isCancelable = (order) => {
  if (!order) {
    return false;
  }

  if (order.outstanding_ar !== 0 || order.outstanding_ap !== 0) {
    return false;
  }

  const shipments = order.shipments || [];
  const hasOpenShipments = shipments.find(
    (s) => s.carrier && s.current && !s.canceled,
  );
  return !hasOpenShipments;
};

class AddressInput extends Component {
  constructor(props) {
    super(props);
    this.state = {
      suggestions: [
        { name: this.props.address.name, address: this.props.address },
      ],
      name: this.props.address ? this.props.address.name : '',
      address: this.props.address ? this.props.address.address : '',
      place_id: this.props.address ? this.props.address.place_id : undefined,
    };
  }

  handleAutosuggestedItem = (item) => {
    const placeIdObject = item?.place_id ? { place_id: item?.place_id } : {};
    this.setState(
      Object.assign(
        { suggestions: [item], address: item.address },
        placeIdObject,
      ),
    );
  };

  render() {
    return (
      <Translation>
        {(t, { i18n: _i18n }) => (
          <div className="flex flex-col">
            <input
              className="w-full pl-4 pr-2 mb-2 border rounded shadow-none appearance-none border-grey h-9plus text-grey-darker"
              value={this.state.name}
              placeholder={titleize(t('NAME'))}
              onChange={(e) => {
                this.setState({ name: e.target.value });
              }}
            />
            <LocationAutosuggest
              key={this.state.address}
              id="autosuggest-address"
              name={this.state.name}
              address={this.state.address}
              value={this.state.address}
              suggestions={[
                { name: this.props.address.name, address: this.props.address },
              ]}
              callbackSelectedItem={this.handleAutosuggestedItem}
            />
            <div className="flex flex-row justify-end mt-2">
              <button
                className="button button-green"
                onClick={() => {
                  const newAddress = {
                    name: this.state.name,
                    address: this.state.address,
                    place_id: this.state.place_id,
                  };
                  OrdersActionCreators.updateOrder(this.props.id, {
                    [this.props.type]: newAddress,
                  });
                  this.props.toggleEditing();
                }}
              >
                {titleize(t('SAVE'))}
              </button>
              <button
                className="bg-white button text-grey hover:text-grey-darker"
                onClick={() => this.props.toggleEditing()}
              >
                {titleize(t('CANCEL'))}
              </button>
            </div>
          </div>
        )}
      </Translation>
    );
  }
}

AddressInput.propTypes = {
  address: PropTypes.shape({
    address: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  id: PropTypes.string,
  type: PropTypes.string,
  toggleEditing: PropTypes.func.isRequired,
};

const Address = (props) => {
  if (!props.address) return null;
  const { address, stop_type } = props;
  return (
    <div className="flex-1">
      <div className="my-1">
        <strong data-testid={`${stop_type}-address-name`}>
          {address.name}
        </strong>
      </div>
      <div className="my-1" data-testid={`${stop_type}-address`}>
        {address.address}
      </div>
    </div>
  );
};

Address.propTypes = {
  address: PropTypes.shape({
    address: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
};

const ShipperCarrierEdit = (props) => {
  let placeholder = '';
  if (props.company) {
    placeholder =
      props.type === 'shipper'
        ? props.company.company_name
        : props.company.name;
  }
  return (
    <Translation>
      {(t, { i18n: _i18n }) => (
        <CustomerSearch
          clearOnSelect
          placeholder={placeholder || props?.placeholder}
          assignField
          onSelect={(item, value) => {
            // Only apply 'Trained' status validation for carriers, not shippers
            if (props.type === 'carrier' && value.status !== 'Trained')
              return showToast(
                t('CARRIER_IS_NOT_TRAINED'),
                t('CARRIER_IS_NOT_TRAINED'),
                'error',
              );
            OrdersActionCreators.updateOrder(props.id, {
              [props.type]: value._id,
            });
            props.toggleEditing();
          }}
          type={props.type + 's'}
          inputStyle={{
            height: '26px',
            paddingLeft: '3px',
            paddingTop: '0px',
            paddingBottom: '0px',
          }}
        />
      )}
    </Translation>
  );
};

ShipperCarrierEdit.propTypes = {
  type: PropTypes.string,
  company: PropTypes.object,
  id: PropTypes.string,
  toggleEditing: PropTypes.func,
};

const orderClosedStatus = ['canceled', 'complete'];

class StatusBar extends Component {
  static propTypes = {
    order: PropTypes.object,
    onArrivedClick: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      isEditingArriving: false,
      isEditingDelivering: false,
      showEditArrivingIcon: false,
      showEditDeliveringIcon: false,
      isEditingShipper: false,
      isEditingCarrier: false,
      showEditShipperIcon: false,
      showEditCarrierIcon: false,
      showCancelModal: false,
      showOverrideBillingStatusModal: false,
    };
  }

  toggleEditing = (type) => {
    this.setState({
      [type]: !this.state[type],
    });
  };

  handleSelect = (order, key) => {
    switch (key) {
      case 'cancel':
        this.setState({ showCancelModal: true });
        break;
      case 'reopen':
        OrdersActionCreators.reopenOrder(order._id);
        break;
      case 'override_billing_status':
        this.setState({ showOverrideBillingStatusModal: true });
        break;
      default:
        break;
    }
  };

  workflowAction = (workflow) => {
    switch (workflow) {
      case 'arrived':
        this.props.onArrivedClick();
        break;
      default:
        OrdersActionCreators.transitionOrder(this.props.order, workflow);
    }
  };

  render() {
    if (!this.props.order) {
      return null;
    }
    const { order, shipments, t } = this.props;
    let shipperName = '';
    if (order.shipper && order.shipper.company_name) {
      shipperName = unescapeHTML(order.shipper.company_name);
    }

    let carrierName = '';
    if (order.carrier && order.carrier.name) {
      carrierName = unescapeHTML(order.carrier.name);
    }

    const status = order.status;

    return (
      <div className="flex flex-col flex-wrap p-4 mb-1 md:flex-row sm:p-6">
        <div className="flex-1 px-2">
          <div className="mb-4">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="my-0">{order.order_id}</h3>
              <ShipmentStatusBadge
                status={status}
                label={titleize(t(sluggify(status)))}
              />
              {isLegManagementEnabled(this.props.order) && (
                <BillingStatusRenderer
                  order={order}
                  isOrderContext
                  onRefreshSuccess={() =>
                    OrdersActionCreators.getOrder(order._id)
                  }
                />
              )}
            </div>
          </div>

          <div>
            <div>
              <div
                className="flex flex-row items-center min-h-28px"
                onMouseEnter={() => {
                  this.setState({ showEditShipperIcon: true });
                }}
                onMouseLeave={() => {
                  this.setState({ showEditShipperIcon: false });
                }}
              >
                <span className="mr-1 text-sm font-medium text-gray-500">
                  {titleize(t('SHIPPER', { count: 1 }))}:
                </span>
                {!this.state.isEditingShipper ? (
                  <a
                    className="no-underline"
                    href={order.shipper ? `/users/${order.shipper._id}` : '#'}
                    hidden={!order.shipper || !order.shipper.company_name}
                    data-testid="shipper-link"
                  >
                    <span className="text-green">{shipperName}</span>
                  </a>
                ) : (
                  <ShipperCarrierEdit
                    id={order.id}
                    type="shipper"
                    placeholder={t('SEARCH_FOR_A_SHIPPER')}
                    company={order.shipper}
                    toggleEditing={() => {
                      this.toggleEditing('showEditShipperIcon');
                      this.toggleEditing('isEditingShipper');
                    }}
                  />
                )}
                <UiButton
                  variant="ghost"
                  size="icon"
                  className={`h-6 w-6 ml-1 ${this.state.showEditShipperIcon ? 'opacity-100' : 'opacity-0'}`}
                  onClick={() => this.toggleEditing('isEditingShipper')}
                >
                  <Pencil className="h-3 w-3" />
                </UiButton>
              </div>
            </div>
            <div className="mt-1">
              <div
                className="flex flex-row items-center min-h-28px"
                onMouseEnter={() => {
                  this.setState({ showEditCarrierIcon: true });
                }}
                onMouseLeave={() => {
                  this.setState({ showEditCarrierIcon: false });
                }}
              >
                <span className="mr-1 text-sm font-medium text-gray-500">
                  {titleize(t('CARRIER', { count: 1 }))}:
                </span>
                {!this.state.isEditingCarrier ? (
                  <a
                    className="no-underline"
                    href={order.carrier ? `/users/${order.carrier._id}` : '#'}
                    hidden={!order.carrier || !order.carrier.name}
                    data-testid="carrier-link"
                  >
                    <div className="relative">
                      <span className="text-green">{carrierName}</span>
                      {order.carrier && order.carrier.mvp ? (
                        <img
                          src="/img/cargomatic-circle.png"
                          className="absolute -top-1.5 -right-3 w-3"
                          alt="MVP"
                        />
                      ) : null}
                    </div>
                  </a>
                ) : (
                  <ShipperCarrierEdit
                    id={order.id}
                    type="carrier"
                    placeholder={t('SEARCH_FOR_A_CARRIER')}
                    company={order.carrier}
                    toggleEditing={() => {
                      this.toggleEditing('showEditCarrierIcon');
                      this.toggleEditing('isEditingCarrier');
                    }}
                  />
                )}
                <UiButton
                  variant="ghost"
                  size="icon"
                  className={`h-6 w-6 ml-1 ${this.state.showEditCarrierIcon ? 'opacity-100' : 'opacity-0'}`}
                  onClick={() => this.toggleEditing('isEditingCarrier')}
                >
                  <Pencil className="h-3 w-3" />
                </UiButton>
              </div>
            </div>
            <div className="flex flex-row items-center mt-1 font-bold min-h-28px">
              <span className="mr-1 text-sm font-medium text-gray-500">
                {titleize(t('MARKET', { count: 1 }))}:
              </span>
              <span className="font-normal text-green">
                {order.arriving && order.arriving.length
                  ? order.arriving[0].market.code
                  : null}
              </span>
            </div>
          </div>
        </div>
        <div
          className="flex-1 p-2"
          onMouseEnter={() => this.setState({ showEditArrivingIcon: true })}
          onMouseLeave={() => this.setState({ showEditArrivingIcon: false })}
        >
          <div className="flex-col h-full">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-500">
                {titleize(t('ORIGIN'))}
              </span>
              <UiButton
                variant="ghost"
                size="icon"
                className={`h-6 w-6 ${this.state.showEditArrivingIcon && !this.state.isEditingArriving ? 'opacity-100' : 'opacity-0'}`}
                onClick={() => this.toggleEditing('isEditingArriving')}
              >
                <Pencil className="h-3 w-3" />
              </UiButton>
            </div>
            {order.arriving && order.arriving.length ? (
              !this.state.isEditingArriving ? (
                <Address
                  id={order.id}
                  address={order.arriving[0]}
                  type="arriving"
                  stop_type="origin"
                />
              ) : (
                <AddressInput
                  id={order.id}
                  type="arriving"
                  address={order.arriving[0]}
                  toggleEditing={() => {
                    this.toggleEditing('isEditingArriving');
                    this.toggleEditing('showEditArrivingIcon');
                  }}
                />
              )
            ) : !this.state.isEditingArriving ? (
              <a
                className="text-green"
                href="#"
                onClick={() => {
                  this.toggleEditing('isEditingArriving');
                }}
              >
                {t('ADD_ORIGIN_ADDRESS')}
              </a>
            ) : (
              <AddressInput
                id={order.id}
                type="arriving"
                toggleEditing={() => {
                  this.toggleEditing('isEditingArriving');
                  this.toggleEditing('showEditArrivingIcon');
                }}
              />
            )}
            <OrderDistance
              distance={order.distance}
              routingType={order.routing_type}
              routingConfiguration={order.routing_configuration}
            />
          </div>
        </div>
        <div
          className="flex-1 p-2"
          onMouseEnter={() => this.setState({ showEditDeliveringIcon: true })}
          onMouseLeave={() => this.setState({ showEditDeliveringIcon: false })}
        >
          <div className="flex-col h-full">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-500">
                {titleize(t('DESTINATION'))}
              </span>
              <UiButton
                variant="ghost"
                size="icon"
                className={`h-6 w-6 ${this.state.showEditDeliveringIcon && !this.state.isEditingDelivering ? 'opacity-100' : 'opacity-0'}`}
                onClick={() => this.toggleEditing('isEditingDelivering')}
              >
                <Pencil className="h-3 w-3" />
              </UiButton>
            </div>
            {order.delivering && order.delivering.length ? (
              !this.state.isEditingDelivering ? (
                <Address
                  id={order.id}
                  address={order.delivering[0]}
                  type="delivering"
                  stop_type="destination"
                />
              ) : (
                <AddressInput
                  id={order.id}
                  address={order.delivering[0]}
                  type="delivering"
                  toggleEditing={() => {
                    this.toggleEditing('isEditingDelivering');
                    this.toggleEditing('showEditDeliveringIcon');
                  }}
                />
              )
            ) : !this.state.isEditingDelivering ? (
              <a
                className="text-green"
                href="#"
                onClick={() => {
                  this.toggleEditing('isEditingDelivering');
                }}
              >
                {t('ADD_DESTINATION_ADDRESS')}
              </a>
            ) : (
              <AddressInput
                id={order.id}
                type="delivering"
                toggleEditing={() => {
                  this.toggleEditing('isEditingDelivering');
                  this.toggleEditing('showEditDeliveringIcon');
                }}
              />
            )}
          </div>
        </div>
        <div className="flex-1 px-2">
          <div className="flex-col h-full">
            <div className="flex items-center gap-2 justify-end">
              {!orderClosedStatus.includes(order.status) && (
                <Workflow
                  order={order}
                  shipments={shipments}
                  onTransition={(step) => this.workflowAction(step)}
                />
              )}

              <UiDropdownMenu>
                <DropdownMenuTrigger asChild>
                  <UiButton variant="outline" size="sm">
                    {t('ACTIONS')}
                    <ChevronDown className="ml-1 h-4 w-4" />
                  </UiButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    data-testid="reopenOrder"
                    disabled={!canReopen(order)}
                    onClick={() => this.handleSelect(order, 'reopen')}
                  >
                    {t('RE_OPEN')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="cancelOrder"
                    disabled={!isCancelable(order)}
                    onClick={() => this.handleSelect(order, 'cancel')}
                  >
                    {titleize(t('CANCEL'))}
                  </DropdownMenuItem>
                  {isLegManagementEnabled(this.props.order) && (
                    <DropdownMenuItem
                      disabled={!canOverrideBillingStatus(order)}
                      onClick={() =>
                        this.handleSelect(order, 'override_billing_status')
                      }
                    >
                      {titleize(t('OVERRIDE_BILLING_STATUS'))}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </UiDropdownMenu>
            </div>
          </div>
        </div>
        {this.state.showCancelModal && (
          <CancelOrderModal
            order={order}
            show={this.state.showCancelModal}
            onHide={() => this.setState({ showCancelModal: false })}
          />
        )}
        {canOverrideBillingStatus() &&
          this.state.showOverrideBillingStatusModal && (
            <OverrideBillingStatusModal
              shipmentIds={[]}
              orderId={order.id}
              open={this.state.showOverrideBillingStatusModal}
              onHide={() =>
                this.setState({ showOverrideBillingStatusModal: false })
              }
              onSuccess={() => {
                OrdersActionCreators.getOrder(order._id);
                this.setState({ showOverrideBillingStatusModal: false });
              }}
            />
          )}
      </div>
    );
  }
}

export default withTranslation()(StatusBar);
